import React from 'react';
import { twMerge } from 'tailwind-merge';

export type RoleBadgeRole = 'dom' | 'sub' | 'switch' | 'mentor' | 'admin' | 'moderator';

const ROLE_LABELS: Record<RoleBadgeRole, string> = {
  dom: 'Dom',
  sub: 'Sub',
  switch: 'Switch',
  mentor: 'Mentor',
  admin: 'Admin',
  moderator: 'Mod',
};

export interface RoleBadgeProps {
  role: RoleBadgeRole;
  /** Override the default label (e.g. a community's custom mentor title). */
  label?: string;
  className?: string;
}

/**
 * Maps raw role strings from the API to a badge role, or null for "no badge".
 * Relationship context: org roles (admin/member) collapse onto dom/sub.
 * Community context: admin/moderator are surfaced as-is; plain members get no badge.
 */
export function normalizeRoleBadgeRole(
  raw: string | null | undefined,
  context: 'relationship' | 'community' = 'relationship'
): RoleBadgeRole | null {
  if (!raw) return null;
  const role = raw.toLowerCase();

  if (context === 'community') {
    // community_members.role includes 'owner' (creator / ownership transfer);
    // owners carry admin authority, so they surface with the Admin badge.
    if (role === 'owner') return 'admin';
    if (role === 'admin' || role === 'moderator' || role === 'mentor') return role;
    return null;
  }

  if (role === 'dominant' || role === 'admin' || role === 'dom') return 'dom';
  if (role === 'submissive' || role === 'member' || role === 'sub') return 'sub';
  if (role === 'switch' || role === 'mentor') return role;
  return null;
}

export function RoleBadge({ role, label, className }: RoleBadgeProps) {
  return (
    <span
      className={twMerge(
        'inline-flex items-center h-5 px-2 rounded-[10px] border border-[var(--theme-primary)]',
        'text-[9.5px] font-semibold uppercase tracking-[.05em] text-[var(--theme-primary)] whitespace-nowrap',
        className
      )}
    >
      {label ?? ROLE_LABELS[role]}
    </span>
  );
}
